import {
  websiteDiscoverySchema,
  type WebsiteDiscoveryConfig,
  urlSchema,
} from '@kanbu/schema/contracts';
import { RemovalBehavior, UpdateFrequency } from '@kanbu/schema/enums';
import { DEFAULT_DOCUMENT_WEIGHT } from '@kanbu/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { useDebounce } from '@uidotdev/usehooks';
import {
  Button,
  cn,
  Dialog,
  Input,
  Label,
  Select,
  SelectItem,
  Switch,
  toast,
} from '@utima/ui';
import { Eye, Globe, Info, RefreshCw, Trash2 } from 'lucide-react';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { v7 as uuidV7 } from 'uuid';
import { z } from 'zod';

import { LockedFeatureIcon } from '@/components';
import { Alert } from '@/components/Alert';
import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';
import { useEnumTranslations, useUser } from '@/hooks';
import { useCurrentOrganization } from '@/hooks/useCurrentOrganization';
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures';
import { invalidateKB } from '@/services/invalidateKB';
import { trpc } from '@/services/trpc';
import type { WebsiteRecordValue } from '@/store/slices/documentModalSlice';

import type { AddSourcePanelHandle } from './addSourcePanelHandle';
import { WebsiteDiscoveryOptions } from './WebsiteDiscoveryOptions';
import { WebsiteRecords } from './websiteRecords/WebsiteRecords';
import { formatAutoUpdateCosts } from '../../lib/autoUpdateCostCopy';
import { UPDATE_FREQUENCY_OPTIONS } from '../../lib/updateFrequencyOptions';
import { DISCOVER_OFF_VALUE } from '../AutoUpdateSettingsForm';
import { OverlappingRootsAlert } from '../OverlappingRootsAlert';

const INHERIT_UPDATE_FREQUENCY = '__inherit__' as const;
const ACTIVE_UPDATE_FREQUENCIES = UPDATE_FREQUENCY_OPTIONS.filter(
  value => value !== UpdateFrequency.Never,
);

export interface WebsitePanelHandle extends AddSourcePanelHandle {
  search: (url: string) => Promise<void>;
  invalidate: () => void;
}
export type WebsiteFormProps = {
  searching?: boolean;
  searchError?: string;
  active?: boolean;
  onSearchingChange?: (value: boolean) => void;
  chatIds: string[];
  availableForAllAgents: boolean;
  setOpen: (open: boolean) => void;
  defaultUrl?: string;
  limitReached: boolean;
  projectedLimitReached: boolean;
  onPendingCountChange: (count: number) => void;
  folderId?: string | null;
  /** Renders as a settings-only panel: no own URL input and no own
   * Add/submit button — the shared "Online sources" row owns the URL and
   * drives submission via the imperative handle (Phase R6). */
  embedded?: boolean;
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

export const AddWebsites = memo(
  forwardRef<WebsitePanelHandle, WebsiteFormProps>(function WebsiteForm(
    {
      searching = false,
      searchError,
      active = true,
      onSearchingChange,
      chatIds,
      availableForAllAgents,
      setOpen,
      defaultUrl,
      limitReached,
      projectedLimitReached,
      onPendingCountChange,
      folderId,
      embedded = false,
      onSubmittingChange,
    },
    ref,
  ) {
    const { t } = useLingui();
    const enumTranslations = useEnumTranslations();
    const { isKbAutoUpdatesLocked } = useSubscriptionFeatures();
    const [isCrawling, setIsCrawling] = useState(false);
    const [isUriValid, setIsUriValid] = useState(true);
    const [search, setSearch] = useState('');
    const [updateFrequency, setUpdateFrequency] =
      useState<UpdateFrequency | null>(null);
    const [discoveryFrequency, setDiscoveryFrequency] =
      useState<UpdateFrequency | null>(null);
    const [autoIncludeNewPages, setAutoIncludeNewPages] = useState(false);
    const [removalBehavior, setRemovalBehavior] = useState<RemovalBehavior>(
      RemovalBehavior.Flag,
    );
    const [mirror, setMirror] = useState(false);
    const [discovery, setDiscovery] = useState<WebsiteDiscoveryConfig>(
      websiteDiscoverySchema.parse({ method: 'quick' }),
    );
    const [uri, setUri] = useState(defaultUrl ?? '');
    const [webUrls, setWebUrls] = useState<WebsiteRecordValue[]>([]);
    const [ready, setReady] = useState(false);
    const [discoveryMessage, setDiscoveryMessage] = useState<string>();
    const requestVersion = useRef(0);
    const trackAsWebsite = mirror || discoveryFrequency != null;
    const { mutateAsync: discover, reset: resetMapUrl } =
      trpc.documents.discoverWebsite.useMutation();
    const changeDiscovery = (value: WebsiteDiscoveryConfig) => {
      requestVersion.current++;
      setDiscovery(value);
      setReady(false);
      setIsCrawling(false);
      setDiscoveryMessage(undefined);
      if (value.method === 'single') {
        setDiscoveryFrequency(null);
        setAutoIncludeNewPages(false);
        setMirror(false);
      }
    };

    const { mutateAsync: createWebsite, isPending: isCreatingWebsite } =
      trpc.documents.createWebsite.useMutation({
        onSuccess: invalidateKB,
      });

    const {
      mutateAsync: createWebsiteFolder,
      isPending: isCreatingWebsiteFolder,
    } = trpc.documentFolders.createWebsite.useMutation({
      onSuccess: invalidateKB,
    });

    const selectedUrls = webUrls.filter(url => url.selected);

    const { currentOrganizationId } = useUser();
    const { organization } = useCurrentOrganization();
    const organizationUrl = organization?.url ?? '';
    const { check: checkCost, update: updateCost } = formatAutoUpdateCosts(
      organization?.currency,
    );

    // Debounced so path-scope overlap isn't re-queried on every keystroke
    // once the URL becomes valid (e.g. typing out a long /blog/... path).
    const debouncedUri = useDebounce(uri, 400);
    const canCheckOverlap =
      trackAsWebsite && z.string().url().safeParse(debouncedUri).success;

    const { data: overlapCheck } =
      trpc.documentFolders.checkWebsiteRootOverlap.useQuery(
        { url: debouncedUri },
        { enabled: canCheckOverlap },
      );

    /**
     * Validates the uri.
     */
    const validateUri = useCallback(() => {
      const result = urlSchema.safeParse(uri);

      setIsUriValid(result.success);
      if (result.success) {
        setUri(result.data); // already normalized
      }

      return result.success;
    }, [setUri, uri]);

    /**
     * Changes local search state and updates the store.
     */
    const handleUriChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        requestVersion.current++;
        setReady(false);
        setIsCrawling(false);
        setDiscoveryMessage(undefined);
        setUri(e.target.value);
        setIsUriValid(true);
      },
      [setUri],
    );

    /**
     * Handle the crawl of a website.
     */
    const invalidate = useCallback(() => {
      requestVersion.current++;
      setReady(false);
      setIsCrawling(false);
      setDiscoveryMessage(undefined);
    }, []);
    const handleCrawl = useCallback(
      async (searchUrl?: string) => {
        const parsed = urlSchema.safeParse(searchUrl ?? uri);
        if (!parsed.success) {
          setIsUriValid(false);

          return;
        }
        setUri(parsed.data);
        setIsCrawling(true);
        setDiscoveryMessage(undefined);
        const version = ++requestVersion.current;
        setReady(false);
        try {
          const response = await discover({ url: parsed.data, discovery });
          if (version !== requestVersion.current) return;
          setWebUrls(previous =>
            response.candidates.map(candidate => {
              const existing = previous.find(
                item => item.url === candidate.url,
              );

              return {
                id: existing?.id ?? uuidV7(),
                url: candidate.url,
                kind: candidate.kind,
                selected: existing?.selected ?? true,
                weight: existing?.weight ?? DEFAULT_DOCUMENT_WEIGHT,
              };
            }),
          );
          setDiscoveryMessage(response.reason);
          setReady(true);
        } catch (error) {
          if (version === requestVersion.current)
            setDiscoveryMessage(
              error instanceof Error
                ? error.message
                : t`Search failed. Try again.`,
            );
        } finally {
          if (version === requestVersion.current) setIsCrawling(false);
        }
      },
      [discover, discovery, uri, t],
    );
    useEffect(() => {
      onSearchingChange?.(isCrawling);
    }, [isCrawling, onSearchingChange]);

    /**
     * Reset all urls and search queries.
     */
    const handleReset = useCallback(() => {
      setUri('');
      setSearch('');
      setWebUrls([]);
    }, [setUri, setSearch, setWebUrls]);

    /**
     * Handle the submission of the selected urls.
     */
    const handleAdd = useCallback(async () => {
      if (!ready || !selectedUrls.length) return;
      if (projectedLimitReached && !limitReached) {
        toast.error(
          t`Cannot add more documents. Please remove some selected documents.`,
        );

        return;
      }

      if (limitReached) {
        toast.error(
          t`Cannot add more documents. Please remove some existing documents.`,
        );

        return;
      }

      if (discoveryFrequency != null && isKbAutoUpdatesLocked) {
        toast.error(t`Upgrade your plan to look for new pages on a schedule.`);

        return;
      }

      if (trackAsWebsite && overlapCheck?.exactMatch) {
        toast.error(
          t`A tracked website already exists for this path. Choose a different URL.`,
        );

        return;
      }

      // Basic URI validation first
      if (uri && !selectedUrls.length && !validateUri()) {
        return;
      }

      const orgId = currentOrganizationId;
      if (!orgId) {
        toast.error(t`Cannot determine organization ID.`);

        return;
      }

      // --- Proceed with submission logic ---
      let finalUrls: string[] = [];
      let weights: Record<string, number> = {};

      if (uri && !selectedUrls.length) {
        finalUrls = [uri];
        weights = { [uri]: DEFAULT_DOCUMENT_WEIGHT };
      } else {
        finalUrls = Array.from(new Set(selectedUrls.map(url => url.url)));
        weights = selectedUrls.reduce(
          (acc, url) => {
            acc[url.url] =
              typeof url.weight === 'number'
                ? url.weight
                : DEFAULT_DOCUMENT_WEIGHT;

            return acc;
          },
          {} as Record<string, number>,
        );
      }

      // Now call the mutation (validation passed)
      trackAsWebsite
        ? // Create tracked website folder via documentFolders.createWebsite
          await createWebsiteFolder({
            url: uri,
            discovery,
            weights,
            includePageUrls: finalUrls,
            allDiscoveredUrls: webUrls.map(u => u.url),
            parentId: folderId ?? null,
            chatIds: chatIds,
            availableForAllAgents,
            ...(updateFrequency == null ? {} : { updateFrequency }),
            ...(discoveryFrequency == null ? {} : { discoveryFrequency }),
            autoIncludeNewPages,
            removalBehavior,
          })
        : await createWebsite({
            urls: finalUrls,
            updateFrequency: isKbAutoUpdatesLocked ? null : updateFrequency,
            chats: chatIds,
            availableForAllAgents,
            weights,
            folderId: folderId ?? null,
          });

      // Replace current URL to remove the 'open' query parameter
      const url = new URL(window.location.href);
      url.searchParams.delete('open');
      window.history.replaceState(null, '', url.toString());

      resetMapUrl();
      handleReset();
      setOpen(false);
    }, [
      ready,
      discovery,
      projectedLimitReached,
      limitReached,
      uri,
      selectedUrls,
      webUrls,
      validateUri,
      trackAsWebsite,
      createWebsiteFolder,
      folderId,
      createWebsite,
      chatIds,
      availableForAllAgents,
      updateFrequency,
      discoveryFrequency,
      autoIncludeNewPages,
      removalBehavior,
      resetMapUrl,
      handleReset,
      setOpen,
      t,
      currentOrganizationId,
      isKbAutoUpdatesLocked,
      overlapCheck?.exactMatch,
    ]);

    useImperativeHandle(
      ref,
      () => ({ submit: handleAdd, search: handleCrawl, invalidate }),
      [handleAdd, handleCrawl, invalidate],
    );

    useEffect(() => {
      if (isKbAutoUpdatesLocked && discoveryFrequency != null) {
        setDiscoveryFrequency(null);
        setAutoIncludeNewPages(false);
      }
    }, [isKbAutoUpdatesLocked, discoveryFrequency]);

    // Effect to update the pending count for the parent modal
    useEffect(() => {
      if (!active) return;
      onPendingCountChange(
        ready ? webUrls.filter(url => url.selected).length : 0,
      );
    }, [ready, webUrls, onPendingCountChange, active]);

    useEffect(() => {
      onSubmittingChange?.(isCreatingWebsite || isCreatingWebsiteFolder);
    }, [isCreatingWebsite, isCreatingWebsiteFolder, onSubmittingChange]);

    return (
      <>
        {/* Show Alert if limit reached */}
        {limitReached && (
          <Alert
            variant='warning'
            title={t`Document Limit Reached`}
            className='mb-4'
          >
            <Trans>
              You have reached the maximum document limit for your plan.
            </Trans>
          </Alert>
        )}
        <div className='flex shrink-0 flex-col gap-2'>
          <div className='flex flex-col gap-2'>
            {!embedded && (
              <div className='flex w-full items-end gap-2'>
                <div className='flex w-full flex-col gap-2'>
                  <Label htmlFor='uri'>{t`URL`}:</Label>
                  <Input
                    name='uri'
                    data-cta='admin-add-websites-uri-input'
                    placeholder='https://www.yourwebsite.com'
                    onChange={handleUriChange}
                    value={uri || defaultUrl || organizationUrl}
                    required
                  />
                </div>

                <Button
                  data-cta='admin-add-websites-map-button'
                  size='sm'
                  disabled={isCrawling || !uri}
                  className='whitespace-nowrap'
                  loading={isCrawling}
                  onClick={() => handleCrawl()}
                  icon={<Globe className='size-4' />}
                >
                  <Trans>Find sources</Trans>
                </Button>
              </div>
            )}

            {!embedded && !isUriValid && (
              <span className='text-sm text-red-500'>
                {t`URL is not valid, it should start with https://`}
              </span>
            )}
          </div>
          <WebsiteDiscoveryOptions
            value={discovery}
            onChange={changeDiscovery}
            mirror={trackAsWebsite}
            onMirrorChange={setMirror}
            discoveryEnabled={discoveryFrequency != null}
          >
            <div
              className={cn(
                'grid grid-cols-1 gap-2 sm:grid-cols-3',
                isKbAutoUpdatesLocked && 'opacity-50',
              )}
            >
              <div className='min-w-0'>
                <div className='mb-2 flex items-center gap-2'>
                  <Eye className='size-3.5 shrink-0 text-muted-fg' />
                  <Label
                    htmlFor='discoveryFrequency'
                    className='text-xs font-medium'
                  >
                    <Trans>Look for new sources</Trans>
                  </Label>
                  <LockedFeatureIcon locked={isKbAutoUpdatesLocked} />
                  <SimpleTooltip
                    title={t`Scans for new sources using the saved discovery method and mirrors the website hierarchy. Usage depends on the method and number of visited pages; Follow links also scrapes content during each scan.`}
                  >
                    <Info className='size-3 shrink-0 cursor-help text-muted-fg' />
                  </SimpleTooltip>
                </div>
                <div
                  className={cn(
                    'space-y-2',
                    isKbAutoUpdatesLocked && 'pointer-events-none',
                  )}
                >
                  <Select
                    id='discoveryFrequency'
                    value={discoveryFrequency ?? DISCOVER_OFF_VALUE}
                    disabled={
                      isKbAutoUpdatesLocked || discovery.method === 'single'
                    }
                    onValueChange={v => {
                      if (v === DISCOVER_OFF_VALUE) {
                        setDiscoveryFrequency(null);
                        setAutoIncludeNewPages(false);

                        return;
                      }

                      setDiscoveryFrequency(v as UpdateFrequency);
                    }}
                  >
                    <SelectItem value={DISCOVER_OFF_VALUE}>
                      <Trans>Never</Trans>
                    </SelectItem>
                    {ACTIVE_UPDATE_FREQUENCIES.map(freq => (
                      <SelectItem key={freq} value={freq}>
                        {enumTranslations.updateFrequency[freq]}
                      </SelectItem>
                    ))}
                  </Select>
                  {discoveryFrequency != null && (
                    <div className='space-y-2 border-t border-border pt-2'>
                      {overlapCheck?.exactMatch ? (
                        <Alert
                          variant='danger'
                          title={t`Path already tracked`}
                          description={t`A tracked website already exists for ${overlapCheck.exactMatch.websiteUrl}. Choose a different URL or path.`}
                        />
                      ) : null}
                      {overlapCheck && !overlapCheck.exactMatch ? (
                        <OverlappingRootsAlert
                          overlappingRoots={overlapCheck.overlappingRoots}
                        />
                      ) : null}
                      <div className='flex items-center justify-between gap-3'>
                        <span className='text-xs text-muted-fg'>
                          <Trans>Automatically include new sources</Trans>
                        </span>
                        <Switch
                          id='autoIncludeNewPages'
                          checked={autoIncludeNewPages}
                          disabled={isKbAutoUpdatesLocked}
                          onCheckedChange={setAutoIncludeNewPages}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='min-w-0'>
                <div className='mb-2 flex items-center gap-2'>
                  <RefreshCw className='size-3.5 shrink-0 text-muted-fg' />
                  <Label className='text-xs font-medium'>
                    <Trans>Auto Refresh</Trans>
                  </Label>
                  <LockedFeatureIcon locked={isKbAutoUpdatesLocked} />
                  <SimpleTooltip
                    title={t`How often the content is re-scraped to check for changes. About ${checkCost} per page check, up to ${updateCost} if the page actually changes.`}
                  >
                    <Info className='size-3 shrink-0 cursor-help text-muted-fg' />
                  </SimpleTooltip>
                </div>
                <div
                  className={cn(isKbAutoUpdatesLocked && 'pointer-events-none')}
                >
                  <Select
                    value={updateFrequency ?? INHERIT_UPDATE_FREQUENCY}
                    disabled={isKbAutoUpdatesLocked}
                    onValueChange={v =>
                      setUpdateFrequency(
                        v === INHERIT_UPDATE_FREQUENCY
                          ? null
                          : (v as UpdateFrequency),
                      )
                    }
                  >
                    <SelectItem value={INHERIT_UPDATE_FREQUENCY}>
                      <Trans>Inherit</Trans>
                    </SelectItem>
                    {UPDATE_FREQUENCY_OPTIONS.map(freq => (
                      <SelectItem key={freq} value={freq}>
                        {enumTranslations.updateFrequency[freq]}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>

              <div className='min-w-0'>
                <div className='mb-2 flex items-center gap-2'>
                  <Trash2 className='size-3.5 shrink-0 text-muted-fg' />
                  <Label
                    htmlFor='removalBehavior'
                    className='text-xs font-medium'
                  >
                    <Trans>If Source Removed</Trans>
                  </Label>
                  <LockedFeatureIcon locked={isKbAutoUpdatesLocked} />
                  <SimpleTooltip
                    title={
                      trackAsWebsite
                        ? t`What happens when the source URL is no longer available. Flag marks it for review, Remove deletes it automatically.`
                        : t`Standalone sources inherit this setting from their folder. Sources outside a folder are flagged for review when removed.`
                    }
                  >
                    <Info className='size-3 shrink-0 cursor-help text-muted-fg' />
                  </SimpleTooltip>
                </div>
                <div
                  className={cn(isKbAutoUpdatesLocked && 'pointer-events-none')}
                >
                  <Select
                    id='removalBehavior'
                    value={
                      trackAsWebsite
                        ? removalBehavior
                        : folderId
                          ? '__inherit__'
                          : RemovalBehavior.Flag
                    }
                    disabled={isKbAutoUpdatesLocked || !trackAsWebsite}
                    onValueChange={v =>
                      setRemovalBehavior(v as RemovalBehavior)
                    }
                  >
                    {!trackAsWebsite && folderId && (
                      <SelectItem value='__inherit__'>
                        <Trans>Inherit</Trans>
                      </SelectItem>
                    )}
                    {Object.values(RemovalBehavior).map(behavior => (
                      <SelectItem key={behavior} value={behavior}>
                        {enumTranslations.removalBehavior[behavior]}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
          </WebsiteDiscoveryOptions>

          <WebsiteRecords
            loading={isCrawling || searching}
            message={searchError ?? discoveryMessage}
            failed={!!searchError || (!ready && !!discoveryMessage)}
            stale={!ready && webUrls.length > 0}
            search={search}
            setSearch={setSearch}
            webUrls={webUrls}
            setWebUrls={setWebUrls}
          />
        </div>

        {!embedded && (
          <Dialog.Footer className='z-50 flex flex-row justify-end gap-2 md:flex-row'>
            <Button
              data-cta='admin-add-websites-add-button'
              disabled={
                isCreatingWebsite ||
                isCreatingWebsiteFolder ||
                limitReached ||
                projectedLimitReached ||
                !ready ||
                !selectedUrls.length
              }
              loading={isCreatingWebsite || isCreatingWebsiteFolder}
              onClick={handleAdd}
            >
              {trackAsWebsite ? (
                <Trans>Create tracked website</Trans>
              ) : (
                <Trans>Add</Trans>
              )}
            </Button>
          </Dialog.Footer>
        )}
      </>
    );
  }),
);
